import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { AiTextArea } from "../../../../components/Form/AiTextArea";
import Input from "../../../../components/Form/Input/input";
import Label from "../../../../components/Form/Label";
import { Form, FormGroup } from "../../../../components/Form/styles";
import { ImageField, LANDSCAPE_CROP } from "../../../../components/ImageField";
import { Box } from "../../../../components/Layout";
import SaveBar from "../../../../components/SaveBar";
import { SubHeader } from "../../../../components/SubHeader";
import { AboutSection } from "../../../../model/site";
import { useSection } from "../../hooks/Section";
import { Container } from "./styles";

const AboutPage = () => {
  const { t } = useTranslation();

  const setDefaultValues = useCallback(
    (newly: boolean, section: AboutSection) => {
      if (newly) {
        section.pageTitle = t("pages.editor.about.header.title");
      }
      return section;
    },
    [t]
  );

  const { section, setSection, saveSection, hasUnsavedChanges } = useSection<AboutSection>("about", setDefaultValues);

  const isSaveDisabled = !section.pageTitle && !section.image && !section.featuredText && !section.completeDescription;

  return (
    <Container>
      <SubHeader
        title={t("pages.editor.about.header.title")}
        crumbs={[{ label: t("pages.editor.preview.footer.actions.sections"), to: "/editor/sections" }]}
        hasUnsavedChanges={hasUnsavedChanges}
      />
      <Form>
        <Box borderBottom>
          <FormGroup>
            <Input
              id="pageTitle"
              type="text"
              value={section.pageTitle}
              label={t("pages.editor.about.form.pageTitle")}
              onChange={e =>
                setSection({
                  ...section,
                  pageTitle: e.target.value,
                })
              }
            />
          </FormGroup>
        </Box>
        <Box borderBottom>
          <ImageField
            shape="landscape"
            crop={LANDSCAPE_CROP}
            title={t("components.imageField.title")}
            hint={t("components.imageField.hints.optionalLandscape")}
            image={section.image}
            onChange={image => setSection({ ...section, image })}
          />
        </Box>
        <Box>
          <FormGroup>
            <AiTextArea
              id="featuredText"
              name="featuredText"
              autoComplete="off"
              rows={5}
              value={section.featuredText}
              onChange={featuredText => setSection({ ...section, featuredText })}
              aiField="about.featuredText"
            />
            <Label htmlFor="supportingText" text={t("pages.editor.about.form.featuredText")} />
          </FormGroup>
          <FormGroup>
            <AiTextArea
              id="completeDescription"
              name="completeDescription"
              autoComplete="off"
              rows={5}
              value={section.completeDescription}
              onChange={completeDescription => setSection({ ...section, completeDescription })}
              aiField="about.completeDescription"
            />
            <Label htmlFor="supportingText" text={t("pages.editor.about.form.completeDescription")} />
          </FormGroup>
        </Box>
        <SaveBar onSave={saveSection} disabled={isSaveDisabled} hasUnsavedChanges={hasUnsavedChanges} />
      </Form>
    </Container>
  );
};

export default AboutPage;
